import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Tag,
  Space,
  Button,
  message,
  Card,
  Input,
  DatePicker,
  Select,
  Tooltip,
  Modal,
  Dropdown,
  Badge,
  Row,
  Col,
  Divider,
  Checkbox,
  Statistic,
  AutoComplete
} from 'antd';
import {
  FileExcelOutlined,
  FilePdfOutlined,
  QrcodeOutlined,
  HistoryOutlined,
  EnvironmentOutlined,
  DownloadOutlined,
  TagsOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  FileProtectOutlined,
  MinusCircleOutlined,
  DollarOutlined,
  WarningOutlined,
  AimOutlined,
  EyeOutlined,
  ClearOutlined,
  CarOutlined,
  LeftOutlined,
  RightOutlined,
  ToolOutlined,
  FileZipOutlined,
  CommentOutlined
} from '@ant-design/icons';
import api from '../../../axiosConfig';
import dayjs from '../../../utils/dayjs';
import MapDialog from '../CenterControl/MapDialog';
import NovedadesModal from './NovedadesModal';
import RutaTrazadaView from './RutaTrazadaView';
import HistorialTrasladoDialog from '../Dialog/HistorialTrasladoDialog';
import HistorialControlServicioDialog from './HistorialControlServicioDialog';
import ObservacionesDialog from '../Dialog/ObservacionesDialog';
import { useUser } from '../../../UserContext';
import nativeWebSocket from '../../../websocket-native';
import { TAIInfoBadge } from '../TAI/PlanillaTAIEstado';
import './ControlServiciosTable.css';

const { RangePicker } = DatePicker;
const { Search } = Input;
const { Option } = Select;

const MIME_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Descarga un blob recibido del backend con el nombre indicado
const descargarBlob = (data, nombre, tipo) => {
  const url = window.URL.createObjectURL(tipo ? new Blob([data], { type: tipo }) : new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombre);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Con responseType 'blob' el error del backend llega como Blob: leer su JSON para mostrar el mensaje real
const mensajeErrorBlob = async (error, porDefecto) => {
  const data = error?.response?.data;
  try {
    if (data instanceof Blob) {
      const json = JSON.parse(await data.text());
      return json.message || json.error || porDefecto;
    }
    return data?.message || data?.error || porDefecto;
  } catch {
    return porDefecto;
  }
};

const ControlServiciosTable = () => {
  const { user: currentUser } = useUser();

  // Función para verificar permisos del usuario
  const hasPermission = useCallback(
    (permission) => {
      const module = currentUser?.modulos?.find((m) => m.key === "Control Servicios");
      if (!module || module.estado !== "disponible") return false;

      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Control Servicios" && mp.permiso === permission
      );
    },
    [currentUser]
  );

  const [controles, setControles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [historialVisible, setHistorialVisible] = useState(false);
  const [historialControlId, setHistorialControlId] = useState(null);
  const [historialControlCodigo, setHistorialControlCodigo] = useState(null);
  const [historialTrasladoVisible, setHistorialTrasladoVisible] = useState(false);
  const [historialTrasladoId, setHistorialTrasladoId] = useState(null);
  const [mapaVisible, setMapaVisible] = useState(false);
  const [trasladoSeleccionado, setTrasladoSeleccionado] = useState(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  // Exportar en Excel (línea) por rango de fechas (máximo 1 mes)
  const [rangoExportVisible, setRangoExportVisible] = useState(false);
  const [rangoExport, setRangoExport] = useState(null);
  const [rangoExportInicio, setRangoExportInicio] = useState(null);
  const [rangoExportLoading, setRangoExportLoading] = useState(false);
  const [observacionesTrasladoId, setObservacionesTrasladoId] = useState(null);
  const [novedadesVisible, setNovedadesVisible] = useState(false);
  const [controlSeleccionado, setControlSeleccionado] = useState(null);
  const [rutaTrazadaVisible, setRutaTrazadaVisible] = useState(false);
  const [trasladoIdRuta, setTrasladoIdRuta] = useState(null);
  const [trasladoRuta, setTrasladoRuta] = useState(null);
  const [previsualizacionVisible, setPrevisualizacionVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previsualizacionLoading, setPrevisualizacionLoading] = useState(false);
  const [controlIdPrevisualizacion, setControlIdPrevisualizacion] = useState(null);
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    terminados: 0,
    fallidos: 0,
    cancelados: 0,
    sin_estado: 0,
    recibidos: 0,
    facturados: 0,
    novedades_pendientes: 0,
    validados: 0,
    sin_validar: 0,
    tai_total: 0,
    tai_normal: 0,
  });

  // Estado para zonas disponibles del usuario
  const [zonasUsuario, setZonasUsuario] = useState([]);
  const [zonasLoading, setZonasLoading] = useState(true);
  const [tieneRestriccionZonas, setTieneRestriccionZonas] = useState(false);

  // Estado para vehículos (filtro de móvil)
  const [vehiculos, setVehiculos] = useState([]);
  const [vehiculosCargados, setVehiculosCargados] = useState(false);

  // Estado para EPS (filtro de EPS)
  const [epsList, setEpsList] = useState([]);
  const [epsLoaded, setEpsLoaded] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Inicializar fechas con el día de hoy para evitar cargar todo
  const [filtros, setFiltros] = useState(() => {
    const hoy = dayjs().format('YYYY-MM-DD');
    return {
      search: '',
      estado_servicio: null,
      fecha_inicio: hoy,
      fecha_fin: hoy,
      tag: null,
      con_novedades_pendientes: false,
      con_novedades_resueltas: false, // Nuevo filtro para novedades resueltas/subsanadas
      seguimiento_traslado: null,
      zona: null,
      movil: null,
      eps: null,
      tipo_traslado: null, // 'tai' | 'normal' | null
    };
  });

  useEffect(() => {
    fetchControles();
  }, [pagination.current, pagination.pageSize]);

  // Cargar zonas disponibles para el usuario al montar el componente
  useEffect(() => {
    const fetchZonasUsuario = async () => {
      setZonasLoading(true);
      try {
        const response = await api.get('/control-servicios/zonas-disponibles');
        const { zonas, tiene_restriccion } = response.data;

        setZonasUsuario(zonas || []);
        setTieneRestriccionZonas(tiene_restriccion);

        console.log('✅ Zonas del usuario cargadas:', {
          zonas: zonas,
          tiene_restriccion: tiene_restriccion,
          cantidad: zonas?.length || 0
        });
      } catch (error) {
        console.error('❌ Error cargando zonas del usuario:', error);
        setTieneRestriccionZonas(false);
      } finally {
        setZonasLoading(false);
      }
    };

    fetchZonasUsuario();
  }, []);

  // Función para cargar vehículos (on-demand)
  const fetchVehiculos = async () => {
    try {
      const response = await api.get('/vehiculos/codigo-placa');
      setVehiculos(response.data || []);
    } catch (error) {
      console.error('Error al obtener los vehículos:', error);
      setVehiculos([]);
    }
  };

  const handleMovilFilterOpen = (open) => {
    if (open && !vehiculosCargados) {
      fetchVehiculos();
      setVehiculosCargados(true);
    }
  };

  const fetchEps = async () => {
    try {
      // Cargar EPS filtradas por rol del usuario
      const response = await api.get('/roles/clientes/usuario');
      const { clientes, tiene_restriccion } = response.data;

      if (tiene_restriccion && clientes && clientes.length > 0) {
        // Mapear al formato esperado por el Select
        setEpsList(clientes.map(c => ({ id: c.id, nombre_cliente: c.nombre })));
      } else {
        // Sin restricción, cargar todos los clientes
        const allClientes = await api.get('/clientes/list');
        setEpsList(allClientes.data || []);
      }
    } catch (error) {
      console.error('Error al cargar EPS:', error);
      // Fallback: cargar todos los clientes
      try {
        const fallbackResponse = await api.get('/clientes/list');
        setEpsList(fallbackResponse.data || []);
      } catch (fallbackError) {
        setEpsList([]);
      }
    }
  };

  const handleEpsFilterOpen = (open) => {
    if (open && !epsLoaded) {
      fetchEps();
      setEpsLoaded(true);
    }
  };

  // Ref para el contenedor de la tabla (scroll horizontal)
  const tableContainerRef = useRef(null);

  // Refs para acceder a valores actuales dentro de los handlers WebSocket
  // Esto evita closures obsoletos cuando los filtros o zonas cambian
  const filtrosRef = useRef(filtros);
  const zonasUsuarioRef = useRef(zonasUsuario);
  const tieneRestriccionZonasRef = useRef(tieneRestriccionZonas);

  // Mantener refs sincronizados con el estado
  useEffect(() => { filtrosRef.current = filtros; }, [filtros]);
  useEffect(() => { zonasUsuarioRef.current = zonasUsuario; }, [zonasUsuario]);
  useEffect(() => { tieneRestriccionZonasRef.current = tieneRestriccionZonas; }, [tieneRestriccionZonas]);

  // WebSocket listeners para actualizaciones en tiempo real
  // Se registran UNA sola vez y usan refs para leer valores actuales
  useEffect(() => {
    // Handler para cuando se crea un nuevo control de servicio
    const handleControlCreated = (data) => {
      console.log('📢 Control de Servicio creado (WebSocket):', data);

      const nuevoControl = data.controlServicio;

      if (!nuevoControl) {
        console.warn('⚠️ Evento de creación sin datos del control');
        return;
      }

      // Leer valores actuales desde refs (no del closure)
      const filtrosActuales = filtrosRef.current;
      const zonasActuales = zonasUsuarioRef.current;
      const tieneRestriccion = tieneRestriccionZonasRef.current;

      // === PRIMERO: Validar zonas permitidas por rol (seguridad) ===
      if (tieneRestriccion && zonasActuales.length > 0) {
        const zonaControl = nuevoControl.zona_paciente;
        const zonaPermitida = zonasActuales.find(z => z.nombre === zonaControl);

        console.log('🔍 Validación de zona WebSocket:', {
          zona_control: zonaControl,
          zonas_permitidas: zonasActuales.map(z => z.nombre),
          zona_permitida: !!zonaPermitida
        });

        if (!zonaPermitida) {
          console.log('❌ Control rechazado: zona no permitida para este usuario');
          return;
        }
      }

      // === SEGUNDO: Validar filtros activos ===
      let cumpleFiltros = true;

      // Filtro de búsqueda
      if (filtrosActuales.search) {
        const searchLower = filtrosActuales.search.toLowerCase();
        const coincide =
          (nuevoControl.codigo_pcs?.toLowerCase().includes(searchLower)) ||
          (nuevoControl.nombre_paciente?.toLowerCase().includes(searchLower)) ||
          (nuevoControl.numero_documento_paciente?.toLowerCase().includes(searchLower)) ||
          (nuevoControl.movil_numero?.toLowerCase().includes(searchLower)) ||
          (nuevoControl.numero_autorizacion?.toLowerCase().includes(searchLower)) ||
          (nuevoControl.numero_preautorizacion?.toLowerCase().includes(searchLower));

        if (!coincide) cumpleFiltros = false;
      }

      // Filtro de estado de servicio
      if (filtrosActuales.estado_servicio && nuevoControl.estado_servicio !== filtrosActuales.estado_servicio) {
        cumpleFiltros = false;
      }

      // Filtro de fecha
      if (filtrosActuales.fecha_inicio && filtrosActuales.fecha_fin) {
        const fechaControl = dayjs(nuevoControl.fecha);
        const inicio = dayjs(filtrosActuales.fecha_inicio);
        const fin = dayjs(filtrosActuales.fecha_fin);

        if (fechaControl.isBefore(inicio, 'day') || fechaControl.isAfter(fin, 'day')) {
          cumpleFiltros = false;
        }
      }

      // Filtro de tag/estado auditoría
      const estadoAuditoriaFiltro = nuevoControl.estado_auditoria || 'sin_estado';
      if (filtrosActuales.tag && estadoAuditoriaFiltro !== filtrosActuales.tag) {
        cumpleFiltros = false;
      }

      // Filtro de zona seleccionada en el dropdown
      if (filtrosActuales.zona && nuevoControl.zona_paciente !== filtrosActuales.zona) {
        cumpleFiltros = false;
      }

      // Filtro de móvil (LIKE search)
      if (filtrosActuales.movil) {
        const movilFiltro = filtrosActuales.movil.toLowerCase();
        const movilControl = (nuevoControl.movil_numero || '').toLowerCase();
        if (!movilControl.includes(movilFiltro)) {
          cumpleFiltros = false;
        }
      }

      // Filtro de seguimiento de traslado
      if (filtrosActuales.seguimiento_traslado && nuevoControl.traslado?.seguimiento !== filtrosActuales.seguimiento_traslado) {
        cumpleFiltros = false;
      }

      // Filtro de EPS
      if (filtrosActuales.eps && nuevoControl.eps !== filtrosActuales.eps) {
        cumpleFiltros = false;
      }

      // Filtro de novedades pendientes
      if (filtrosActuales.con_novedades_pendientes) {
        const tieneNovedadesPendientes = (nuevoControl.novedades_pendientes_count || 0) > 0;
        if (!tieneNovedadesPendientes) {
          cumpleFiltros = false;
        }
      }

      if (!cumpleFiltros) {
        console.log('ℹ️ Nuevo control no cumple con los filtros actuales, descartado');
        return;
      }

      // Si cumple todos los filtros, agregarlo al estado
      setControles(prevControles => {
        const existe = prevControles.some(c => c.id === nuevoControl.id);
        if (existe) return prevControles;
        return [nuevoControl, ...prevControles];
      });

      setPagination(prev => ({ ...prev, total: prev.total + 1 }));

      setEstadisticas(prev => ({
        ...prev,
        total: prev.total + 1,
        ...(nuevoControl.estado_servicio === 'terminado' && { terminados: prev.terminados + 1 }),
        ...(nuevoControl.estado_servicio === 'fallido' && { fallidos: prev.fallidos + 1 }),
        ...(nuevoControl.estado_servicio === 'cancelado' && { cancelados: prev.cancelados + 1 }),
        ...(!nuevoControl.estado_auditoria && { sin_estado: prev.sin_estado + 1 }),
        ...(nuevoControl.estado_auditoria === 'recibido' && { recibidos: prev.recibidos + 1 }),
        ...(nuevoControl.estado_auditoria === 'facturado' && { facturados: prev.facturados + 1 }),
      }));

      console.log('✅ Nuevo control agregado al listado:', nuevoControl.codigo_pcs);
      message.success(`Nuevo control creado: ${nuevoControl.codigo_pcs}`);
    };

    // Handler para cuando se actualiza un control de servicio (general)
    const handleControlUpdated = (data) => {
      console.log('📢 Control de Servicio actualizado (WebSocket):', data);

      setControles(prevControles =>
        prevControles.map(control =>
          control.id === data.controlServicioId
            ? { ...control, ...data.controlServicio }
            : control
        )
      );
    };

    // Handler para cuando cambia el tag/estado de auditoría
    const handleTagChanged = (data) => {
      console.log('📢 Tag de Control de Servicio cambiado (WebSocket):', data);

      setControles(prevControles =>
        prevControles.map(control =>
          control.id === data.controlServicioId
            ? { ...control, estado_auditoria: data.tagNuevo }
            : control
        )
      );
    };

    // Handler para cuando cambia el estado de auditoría (recibido/facturado)
    const handleEstadoChanged = (data) => {
      console.log('📢 Estado de auditoría de Control de Servicio cambiado (WebSocket):', data);

      setControles(prevControles =>
        prevControles.map(control =>
          control.id === data.controlServicioId
            ? { ...control, estado_auditoria: data.estadoNuevo }
            : control
        )
      );
    };

    // Handler para validación
    const handleControlValidated = (data) => {
      console.log('📢 Control de Servicio validado (WebSocket):', data);

      setControles(prevControles =>
        prevControles.map(control =>
          control.id === data.controlServicioId
            ? { ...control, ...data.controlServicio }
            : control
        )
      );
    };

    // Handler para rechazo
    const handleControlRejected = (data) => {
      console.log('📢 Control de Servicio rechazado (WebSocket):', data);

      // Actualizar el control en el estado
      setControles(prevControles =>
        prevControles.map(control =>
          control.id === data.controlServicioId
            ? { ...control, ...data.controlServicio }
            : control
        )
      );
    };

    // Contador de observaciones del traslado de cada control
    const handleObservacionesUpdate = ({ trasladoId, observaciones_count }) => {
      if (!trasladoId) return;
      setControles(prevControles =>
        prevControles.map(control =>
          String(control.traslado_id) === String(trasladoId)
            ? { ...control, observaciones_count: observaciones_count || 0 }
            : control
        )
      );
    };

    // Registrar todos los listeners
    nativeWebSocket.onControlServicioCreated(handleControlCreated);
    nativeWebSocket.onObservacionesUpdate(handleObservacionesUpdate);
    nativeWebSocket.onControlServicioUpdated(handleControlUpdated);
    nativeWebSocket.onControlServicioTagChanged(handleTagChanged);
    nativeWebSocket.onControlServicioEstadoChanged(handleEstadoChanged);
    nativeWebSocket.onControlServicioValidated(handleControlValidated);
    nativeWebSocket.onControlServicioRejected(handleControlRejected);

    console.log('✅ WebSocket listeners registrados para Control de Servicios');

    // Cleanup: remover listeners al desmontar el componente
    return () => {
      nativeWebSocket.removeListener('control_servicio.created', handleControlCreated);
      nativeWebSocket.removeListener('control_servicio.updated', handleControlUpdated);
      nativeWebSocket.removeListener('control_servicio.tag_changed', handleTagChanged);
      nativeWebSocket.removeListener('control_servicio.estado_changed', handleEstadoChanged);
      nativeWebSocket.removeListener('control_servicio.validated', handleControlValidated);
      nativeWebSocket.removeListener('control_servicio.rejected', handleControlRejected);
      nativeWebSocket.removeListener('observaciones.updated', handleObservacionesUpdate);
      console.log('🔌 WebSocket listeners removidos para Control de Servicios');
    };
    // Se registra UNA sola vez. Los handlers leen valores actuales desde refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchControles = async () => {
    setLoading(true);
    try {
      // Preparar parámetros, excluyendo filtros booleanos si son false
      const { con_novedades_pendientes, con_novedades_resueltas, ...otrosFiltros } = filtros;
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        ...otrosFiltros,
        // Solo incluir filtros booleanos si son true
        ...(con_novedades_pendientes && { con_novedades_pendientes: true }),
        ...(con_novedades_resueltas && { con_novedades_resueltas: true }),
      };

      const response = await api.get('/control-servicios', { params });

      if (response.data.success) {
        setControles(response.data.data.data);
        setPagination({
          ...pagination,
          total: response.data.data.total,
        });

        // Actualizar estadísticas si están disponibles
        if (response.data.data.stats) {
          setEstadisticas(response.data.data.stats);
        }
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Error al cargar controles de servicio');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  const handleAplicarFiltros = () => {
    setPagination({ ...pagination, current: 1 });
    fetchControles();
  };

  // Limpiar filtros
  const handleLimpiarFiltros = () => {
    const hoy = dayjs().format('YYYY-MM-DD');
    setFiltros({
      search: '',
      estado_servicio: null,
      fecha_inicio: hoy,
      fecha_fin: hoy,
      tag: null,
      con_novedades_pendientes: false,
      con_novedades_resueltas: false,
      seguimiento_traslado: null,
      zona: null,
      movil: null,
      eps: null,
      tipo_traslado: null,
    });
    setPagination({ ...pagination, current: 1 });
  };

  // Actualizar estado de un control
  const handleActualizarTag = async (id, tag) => {
    try {
      await api.put(`/control-servicios/${id}/tag`, { tag });
      message.success('Estado actualizado correctamente');
      fetchControles();
    } catch (error) {
      // Mostrar mensaje de error del backend si está disponible
      const errorMsg = error.response?.data?.message || 'Error al actualizar estado';
      message.error(errorMsg);
      console.error(error);
    }
  };

  // Quitar etiqueta (requiere permiso especial QUITAR_ETIQUETA_CS)
  const handleQuitarEtiqueta = (id) => {
    Modal.confirm({
      title: '¿Está seguro de quitar la etiqueta?',
      content: 'Esta acción quitará completamente la etiqueta del control de servicio, incluso si está en estado "Facturado". Esta acción requiere permisos especiales.',
      okText: 'Sí, quitar etiqueta',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.put(`/control-servicios/${id}/quitar-etiqueta`);
          message.success('Etiqueta eliminada correctamente');
          fetchControles();
        } catch (error) {
          const errorMsg = error.response?.data?.message || 'Error al quitar etiqueta';
          message.error(errorMsg);
          console.error(error);
        }
      },
    });
  };

  // Facturar masivamente
  const handleFacturarMasivo = () => {
    // Verificar que los seleccionados estén en estado "recibido"
    const seleccionados = controles.filter(c => selectedRowKeys.includes(c.id));
    const noRecibidos = seleccionados.filter(c => c.estado_auditoria !== 'recibido');

    if (noRecibidos.length > 0) {
      const codigos = noRecibidos.map(c => c.codigo_pcs).join(', ');
      message.error(`Los siguientes controles no están en estado "Recibido": ${codigos}`);
      return;
    }

    Modal.confirm({
      title: 'Facturar Masivamente',
      icon: <ExclamationCircleOutlined />,
      content: `¿Está seguro de facturar ${selectedRowKeys.length} controles de servicio seleccionados? Esta acción cambiará su estado de "Recibido" a "Facturado".`,
      okText: 'Sí, facturar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const response = await api.post('/control-servicios/facturar-masivo', {
            ids: selectedRowKeys,
          });
          message.success(response.data.message || 'Controles facturados correctamente');
          setSelectedRowKeys([]);
          fetchControles();
        } catch (error) {
          const errorMsg = error.response?.data?.message || 'Error al facturar masivamente';
          message.error(errorMsg);
          console.error(error);
        }
      },
    });
  };

  // Descargar PDF individual
  const handleDescargarPDF = async (id) => {
    try {
      const response = await api.get(`/control-servicios/${id}/descargar-pdf`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Control_Servicio_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success('PDF descargado exitosamente');
    } catch (error) {
      message.error('Error al descargar PDF');
      console.error(error);
    }
  };

  // Descargar Excel individual
  const handleDescargarExcel = async (id) => {
    try {
      const response = await api.get(`/control-servicios/${id}/descargar-excel`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Control_Servicio_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success('Excel descargado exitosamente');
    } catch (error) {
      message.error('Error al descargar Excel');
      console.error(error);
    }
  };

  // Previsualizar PDF del control de servicio
  const handlePrevisualizarPDF = async (id) => {
    setPrevisualizacionLoading(true);
    setPrevisualizacionVisible(true);
    setControlIdPrevisualizacion(id);
    try {
      const response = await api.get(`/control-servicios/${id}/visualizar-pdf`, {
        responseType: 'blob',
      });

      // Crear URL temporal para el blob
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
    } catch (error) {
      message.error('Error al cargar la previsualización');
      console.error(error);
      setPrevisualizacionVisible(false);
    } finally {
      setPrevisualizacionLoading(false);
    }
  };

  // Cerrar modal de previsualización
  const handleCerrarPrevisualizacion = () => {
    setPrevisualizacionVisible(false);
    setControlIdPrevisualizacion(null);
    // Liberar la URL del blob
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  // Abrir modal de selección de tipo de exportación
  const handleExportacionMasiva = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Seleccione al menos un control de servicio');
      return;
    }
    setExportModalVisible(true);
  };

  // Exportación separada (actual - múltiples PDFs en ZIP)
  const handleExportarSeparados = async () => {
    setExportLoading(true);
    try {
      const response = await api.post(
        '/control-servicios/exportar-masivo',
        { ids: selectedRowKeys },
        { responseType: 'blob' }
      );

      descargarBlob(response.data, `Controles_Servicio_${dayjs().format('YYYYMMDD_HHmmss')}.zip`);

      message.success(`${selectedRowKeys.length} controles exportados exitosamente`);
      setSelectedRowKeys([]);
      setExportModalVisible(false);
    } catch (error) {
      message.error(await mensajeErrorBlob(error, 'Error al exportar controles'));
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  // Exportación unificada (nuevo - un solo PDF con múltiples páginas)
  const handleExportarUnificado = async () => {
    setExportLoading(true);
    try {
      const response = await api.post(
        '/control-servicios/exportar-unificado',
        { ids: selectedRowKeys },
        { responseType: 'blob' }
      );

      descargarBlob(response.data, `Controles_Servicio_Unificado_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`);

      message.success(`PDF unificado con ${selectedRowKeys.length} controles generado exitosamente`);
      setSelectedRowKeys([]);
      setExportModalVisible(false);
    } catch (error) {
      message.error(await mensajeErrorBlob(error, 'Error al generar PDF unificado'));
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  // Exportación en Excel "en línea": una fila por control con todos sus datos en columnas
  const handleExportarExcelLinea = async () => {
    if (selectedRowKeys.length > 1000) {
      message.warning('Puede exportar hasta 1000 controles de servicio a la vez');
      return;
    }
    setExportLoading(true);
    try {
      const response = await api.post(
        '/control-servicios/exportar-excel',
        { ids: selectedRowKeys },
        { responseType: 'blob' }
      );

      descargarBlob(response.data, `Controles_Servicio_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`, MIME_XLSX);

      message.success(`Excel con ${selectedRowKeys.length} controles generado exitosamente`);
      setSelectedRowKeys([]);
      setExportModalVisible(false);
    } catch (error) {
      message.error(await mensajeErrorBlob(error, 'Error al generar el Excel'));
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  // Exportación en Excel "en línea" por rango de fechas (máximo 1 mes): todos los controles del rango
  const handleAbrirExportarRango = () => {
    const inicio = dayjs(filtros.fecha_inicio);
    const fin = dayjs(filtros.fecha_fin);
    // Propone el rango del listado si cabe en el máximo permitido
    setRangoExport(inicio.isValid() && fin.isValid() && !fin.isAfter(inicio.add(1, 'month')) ? [inicio, fin] : null);
    setRangoExportInicio(null);
    setRangoExportVisible(true);
  };

  const rangoExportDeshabilitado = (current) => {
    if (!rangoExportInicio || !current) return false;
    return current.isAfter(rangoExportInicio.add(1, 'month'), 'day') || current.isBefore(rangoExportInicio.subtract(1, 'month'), 'day');
  };

  const handleExportarExcelRango = async () => {
    if (!rangoExport?.[0] || !rangoExport?.[1]) {
      message.warning('Seleccione un rango de fechas');
      return;
    }
    const [inicio, fin] = rangoExport;
    if (fin.isAfter(inicio.add(1, 'month'), 'day')) {
      message.warning('El rango máximo permitido es de 1 mes');
      return;
    }
    setRangoExportLoading(true);
    try {
      const response = await api.post(
        '/control-servicios/exportar-excel-rango',
        { fecha_inicio: inicio.format('YYYY-MM-DD'), fecha_fin: fin.format('YYYY-MM-DD') },
        { responseType: 'blob', timeout: 300000 }
      );

      descargarBlob(response.data, `Controles_Servicio_${inicio.format('YYYYMMDD')}_${fin.format('YYYYMMDD')}.xlsx`, MIME_XLSX);

      message.success('Excel de controles de servicio generado exitosamente');
      setRangoExportVisible(false);
    } catch (error) {
      message.error(await mensajeErrorBlob(error, 'Error al generar el Excel'));
      console.error(error);
    } finally {
      setRangoExportLoading(false);
    }
  };

  // Ver historial del Control de Servicio
  const handleVerHistorial = (controlServicioId, codigoPcs) => {
    setHistorialControlId(controlServicioId);
    setHistorialControlCodigo(codigoPcs);
    setHistorialVisible(true);
  };

  // Ver historial del Traslado asociado
  const handleVerHistorialTraslado = (trasladoId) => {
    setHistorialTrasladoId(trasladoId);
    setHistorialTrasladoVisible(true);
  };

  // Ver mapa del control servicio
  const handleVerMapa = (record) => {
    // Verificar que el control servicio tenga coordenadas
    if (record.latitud_origen && record.longitud_origen &&
        record.latitud_destino && record.longitud_destino) {

      // Limpiar estado anterior primero
      setTrasladoSeleccionado(null);
      setMapaVisible(false);

      // Usar setTimeout para asegurar que el estado se limpie antes de mostrar nuevo
      setTimeout(() => {
        setTrasladoSeleccionado(record);
        setMapaVisible(true);
      }, 50);
    } else {
      message.warning('El control de servicio no tiene coordenadas de origen y destino');
    }
  };

  // Visualizar QR
  const handleVisualizarQR = (codigoQR) => {
    const url = `/validar-control-servicio/${codigoQR}`;
    window.open(url, '_blank');
  };

  // Abrir modal de novedades
  const handleAbrirNovedades = (record) => {
    setControlSeleccionado(record);
    setNovedadesVisible(true);
  };

  // Ver ruta GPS trazada
  const handleVerRutaTrazada = async (record) => {
    if (record.traslado_id) {
      // Limpiar estado anterior primero
      setRutaTrazadaVisible(false);
      setTrasladoIdRuta(null);
      setTrasladoRuta(null);

      // Obtener datos del traslado ANTES de mostrar el modal
      try {
        const response = await api.get(`/traslados/${record.traslado_id}`);
        if (response.data) {
          // Ahora sí establecer los datos y mostrar el modal
          setTrasladoIdRuta(record.traslado_id);
          setTrasladoRuta(response.data);
          setRutaTrazadaVisible(true);
        }
      } catch (error) {
        console.error('Error al obtener datos del traslado:', error);
        message.error('Error al cargar datos de la ruta');
      }
    } else {
      message.warning('Este control de servicio no tiene un traslado asociado');
    }
  };

  // Cerrar modal de ruta trazada
  const handleCerrarRutaTrazada = () => {
    setRutaTrazadaVisible(false);
    setTrasladoIdRuta(null);
    setTrasladoRuta(null);
  };

  // Cerrar modal de novedades y recargar tabla
  const handleCerrarNovedades = () => {
    setNovedadesVisible(false);
    setControlSeleccionado(null);
    // Recargar la tabla para actualizar contadores de novedades
    fetchControles();
  };

  // Corregir coordenadas de ruta GPS (usar coordenadas planificadas del ControlServicio)
  const handleCorregirRuta = (record) => {
    // Verificar si tiene novedades registradas
    const tieneNovedades = (record.novedades_pendientes_count || 0) + (record.novedades_resueltas_count || 0) > 0;

    if (!tieneNovedades) {
      message.warning('Debe registrar al menos una novedad antes de corregir la ruta');
      return;
    }

    Modal.confirm({
      title: 'Corregir Coordenadas GPS de Ruta',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <p>Esta acción corregirá las coordenadas GPS incorrectas registradas por el conductor.</p>
          <p><strong>Se tomarán las coordenadas de "Ver Mapa"</strong> (las planificadas/correctas) y se actualizarán los puntos de inicio/fin en la ruta GPS.</p>
          <p>Los cambios se reflejarán en:</p>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Botón "Ver Ruta GPS"</li>
            <li>Mapa del código QR</li>
          </ul>
          <p style={{ color: '#ff4d4f', fontWeight: 500 }}>¿Está seguro de continuar?</p>
        </div>
      ),
      okText: 'Sí, corregir ruta GPS',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const response = await api.post(`/control-servicios/${record.id}/corregir-ruta`);
          if (response.data.success) {
            // Si una coordenada planificada falta o está fuera de Colombia, el backend la omite y lo explica
            if (response.data.data?.omitidos?.length) {
              message.warning({ content: response.data.message, duration: 10 });
            } else {
              message.success('Coordenadas GPS corregidas. Los cambios se verán en "Ver Ruta GPS" y el QR.');
            }
            fetchControles();
          } else {
            message.error(response.data.message || 'Error al corregir la ruta');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.message || 'Error al corregir la ruta';
          message.error(errorMsg);
          console.error('Error al corregir ruta:', error);
        }
      },
    });
  };

  // Menu de estados
  const getTagMenu = (record) => {
    const estadoActual = record.estado_auditoria;
    const items = [];

    // Flujo de estados: Sin Estado → Recibido → Facturado (one-way)
    // Cada opción requiere su permiso específico

    // Solo mostrar "Recibido" si no tiene estado Y tiene permiso RECIBIDO_ETIQUETA_CS
    if (!estadoActual && hasPermission('RECIBIDO_ETIQUETA_CS')) {
      items.push({
        key: 'recibido',
        label: 'Recibido',
        icon: <TagsOutlined style={{ color: '#52c41a' }} />,
        onClick: () => handleActualizarTag(record.id, 'recibido'),
      });
    }

    // Solo mostrar "Facturado" si ya está en "Recibido" Y tiene permiso FACTURADO_ETIQUETA_CS
    if (estadoActual === 'recibido' && hasPermission('FACTURADO_ETIQUETA_CS')) {
      items.push({
        key: 'facturado',
        label: 'Facturado',
        icon: <TagsOutlined style={{ color: '#1890ff' }} />,
        onClick: () => handleActualizarTag(record.id, 'facturado'),
      });
    }

    // Opción para quitar estado (solo si está en "Recibido" y NO tiene permiso de admin)
    // Si tiene permiso de admin, solo usa la opción "Quitar Etiqueta (Admin)"
    if (estadoActual === 'recibido' && !hasPermission('QUITAR_ETIQUETA_CS')) {
      if (items.length > 0) {
        items.push({
          type: 'divider',
        });
      }
      items.push({
        key: 'quitar',
        label: 'Quitar estado',
        onClick: () => handleActualizarTag(record.id, null),
      });
    }

    // Opción especial: "Quitar Etiqueta" - Solo disponible con permiso QUITAR_ETIQUETA_CS
    // Esta opción permite quitar la etiqueta incluso si está en "Facturado"
    if (estadoActual && hasPermission('QUITAR_ETIQUETA_CS')) {
      if (items.length > 0) {
        items.push({
          type: 'divider',
        });
      }
      items.push({
        key: 'quitar-etiqueta',
        label: 'Quitar Etiqueta',
        icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
        onClick: () => handleQuitarEtiqueta(record.id),
        danger: true,
      });
    }

    return { items };
  };

  // Renderizar estado de auditoría
  const renderAuditoriaTag = (record) => {
    const estadoColors = {
      recibido: 'success',       // Verde
      facturado: 'processing',   // Azul
    };

    const estadoLabels = {
      recibido: 'RECIBIDO',
      facturado: 'FACTURADO',
    };

    if (record.estado_auditoria) {
      return (
        <Tag color={estadoColors[record.estado_auditoria]}>
          {estadoLabels[record.estado_auditoria]}
        </Tag>
      );
    }
    return <Tag color="default">SIN ESTADO</Tag>;
  };

  const columns = [
    {
      title: 'Código PCS',
      dataIndex: 'codigo_pcs',
      key: 'codigo_pcs',
      fixed: 'left',
      width: 150,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Pre-Autorización',
      dataIndex: 'numero_preautorizacion',
      key: 'preautorizacion',
      width: 150,
      render: (text) => text || '-',
    },
    {
      title: 'Autorización',
      dataIndex: 'numero_autorizacion',
      key: 'autorizacion',
      width: 140,
      render: (text) => text || '-',
    },
    {
      title: 'Paciente',
      dataIndex: 'nombre_paciente',
      key: 'nombre_paciente',
      width: 200,
    },
    {
      title: 'Documento',
      dataIndex: 'numero_documento_paciente',
      key: 'documento',
      width: 120,
    },
    {
      title: 'EPS',
      dataIndex: 'eps',
      key: 'eps',
      width: 150,
    },
    {
      title: 'Zona',
      dataIndex: 'zona_paciente',
      key: 'zona_paciente',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: 'Tipo',
      key: 'tipo_tai',
      width: 130,
      align: 'center',
      render: (_, record) => (
        <TAIInfoBadge
          esTAI={record.es_tai}
          transportadoraNombre={record.transportadora_nombre}
          trayectoTipo={record.tai_trayecto_tipo}
          numeroTiquete={record.tai_numero_tiquete}
          planillaEstado={record.planilla_estado}
        />
      ),
    },
    {
      title: 'Conductor',
      dataIndex: 'nombre_conductor',
      key: 'nombre_conductor',
      width: 200,
    },
    {
      title: 'Móvil/Placa',
      key: 'movil_placa',
      width: 120,
      render: (_, record) => `${record.movil_numero} / ${record.placa}`,
    },
    {
      title: 'Estado Servicio',
      dataIndex: 'estado_servicio',
      key: 'estado_servicio',
      width: 130,
      render: (estado, record) => {
        let tag;
        if (estado === 'terminado') {
          tag = <Tag color="success">TERMINADO</Tag>;
        } else if (estado === 'cancelado') {
          tag = <Tag color="#ff4d4f" style={{ fontWeight: 'bold' }}>CANCELADO</Tag>;
        } else {
          tag = <Tag color="#9B1C31" style={{ fontWeight: 'bold' }}>FALLIDO</Tag>;
        }
        if (!record.firma_pendiente_otp) return tag;
        // Novedad "firma ilegible del paciente": firma retirada hasta cerrar el traslado con OTP
        return (
          <Space direction="vertical" size={2}>
            {tag}
            <Tooltip title="Firma retirada por novedad. Se repone al cerrar el traslado con OTP desde Coordinación o Centro de Control.">
              <Tag color="magenta" style={{ fontWeight: 'bold' }}>FIRMA PENDIENTE OTP</Tag>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Validación',
      key: 'validacion_traslado',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const seguimiento = record.traslado?.seguimiento;
        if (seguimiento === 'Validado') {
          return (
            <Tooltip title="Traslado Validado">
              <Tag icon={<CheckCircleOutlined />} color="success">
                VALIDADO
              </Tag>
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title="Traslado Sin Validar">
              <Tag icon={<WarningOutlined />} color="warning">
                SIN VALIDAR
              </Tag>
            </Tooltip>
          );
        }
      },
    },
    {
      title: 'Estado Auditoría',
      key: 'estado_auditoria',
      width: 150,
      render: (_, record) => renderAuditoriaTag(record),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 480,
      render: (_, record) => {
        // Determinar color del icono de novedades
        const tieneNovedadesPendientes = (record.novedades_pendientes_count || 0) > 0;
        const tieneNovedadesResueltas = (record.novedades_resueltas_count || 0) > 0;
        let novedadesColor = '#8c8c8c'; // Gris por defecto (sin novedades)
        let novedadesIcon = <ExclamationCircleOutlined />;

        if (tieneNovedadesPendientes) {
          novedadesColor = '#ff4d4f'; // Rojo si hay pendientes
        } else if (tieneNovedadesResueltas) {
          novedadesColor = '#52c41a'; // Verde si todas están resueltas
          novedadesIcon = <CheckCircleOutlined />;
        }

        return (
          <Space size="small" style={{ justifyContent: 'flex-start' }}>
            <Tooltip title="Previsualizar PCS">
              <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handlePrevisualizarPDF(record.id)}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
            <Tooltip title="Descargar PDF">
              <Button
                type="primary"
                size="small"
                icon={<FilePdfOutlined />}
                onClick={() => handleDescargarPDF(record.id)}
              />
            </Tooltip>
            <Tooltip title="Descargar Excel">
              <Button
                type="default"
                size="small"
                icon={<FileExcelOutlined />}
                onClick={() => handleDescargarExcel(record.id)}
              />
            </Tooltip>
            <Tooltip title="Ver QR">
              <Button
                type="default"
                size="small"
                icon={<QrcodeOutlined />}
                onClick={() => handleVisualizarQR(record.codigo_qr)}
              />
            </Tooltip>
            <Tooltip title="Historial Control">
              <Button
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => handleVerHistorial(record.id, record.codigo_pcs)}
              />
            </Tooltip>
            {record.traslado_id && (
              <Tooltip title="Historial Traslado">
                <Button
                  size="small"
                  icon={<HistoryOutlined />}
                  onClick={() => handleVerHistorialTraslado(record.traslado_id)}
                  style={{ color: '#13c2c2' }}
                />
              </Tooltip>
            )}
            {record.traslado_id && hasPermission('VER_OBSERVACIONES_CS') && (() => {
              const observacionesCount = record.observaciones_count || 0;
              return (
                <Tooltip title="Observaciones del traslado">
                  <Badge
                    count={observacionesCount}
                    size="small"
                    style={{ backgroundColor: observacionesCount > 0 ? '#52c41a' : '#d9d9d9' }}
                  >
                    <Button
                      size="small"
                      icon={<CommentOutlined />}
                      onClick={() => setObservacionesTrasladoId(record.traslado_id)}
                      style={{ color: observacionesCount > 0 ? '#1890ff' : '#8c8c8c' }}
                    />
                  </Badge>
                </Tooltip>
              );
            })()}
            <Tooltip title="Ver Ruta GPS">
              <Button
                size="small"
                icon={<AimOutlined />}
                onClick={() => handleVerRutaTrazada(record)}
                style={{ color: '#722ed1' }}
              />
            </Tooltip>
            <Tooltip title="Ver Mapa">
              <Button
                size="small"
                icon={<EnvironmentOutlined />}
                onClick={() => handleVerMapa(record)}
              />
            </Tooltip>
            <Tooltip title={
              tieneNovedadesPendientes
                ? `Novedades (${record.novedades_pendientes_count} pendientes)`
                : tieneNovedadesResueltas
                  ? `Novedades (${record.novedades_resueltas_count} resueltas)`
                  : 'Novedades'
            }>
              <Badge
                count={tieneNovedadesPendientes ? record.novedades_pendientes_count : 0}
                offset={[-5, 5]}
              >
                <Button
                  size="small"
                  icon={novedadesIcon}
                  onClick={() => handleAbrirNovedades(record)}
                  style={{ color: novedadesColor }}
                />
              </Badge>
            </Tooltip>
            {hasPermission('CORREGIR_RUTA_CS') && (
              <Tooltip title={
                (tieneNovedadesPendientes || tieneNovedadesResueltas)
                  ? 'Corregir Ruta GPS - Corrige coordenadas incorrectas del conductor'
                  : 'Corregir Ruta GPS (requiere registrar novedad primero)'
              }>
                <Button
                  size="small"
                  icon={<ToolOutlined />}
                  onClick={() => handleCorregirRuta(record)}
                  disabled={!tieneNovedadesPendientes && !tieneNovedadesResueltas}
                  style={{
                    color: (tieneNovedadesPendientes || tieneNovedadesResueltas) ? '#fa8c16' : '#d9d9d9',
                    borderColor: (tieneNovedadesPendientes || tieneNovedadesResueltas) ? '#fa8c16' : '#d9d9d9',
                  }}
                />
              </Tooltip>
            )}
            <Dropdown menu={getTagMenu(record)} trigger={['click']}>
              <Button size="small" icon={<TagsOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  // Selección de filas
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
  };

  // Funciones para scroll horizontal de la tabla
  const getScrollableElement = () => {
    if (!tableContainerRef.current) return null;
    // Ant Design Table con scroll horizontal usa .ant-table-content como contenedor scrollable
    return tableContainerRef.current.querySelector('.ant-table-content') ||
           tableContainerRef.current.querySelector('.ant-table-body') ||
           tableContainerRef.current.querySelector('.ant-table-wrapper');
  };

  const handleScrollLeft = () => {
    const scrollElement = getScrollableElement();
    if (scrollElement) {
      scrollElement.scrollLeft = Math.max(0, scrollElement.scrollLeft - 400);
    }
  };

  const handleScrollRight = () => {
    const scrollElement = getScrollableElement();
    if (scrollElement) {
      scrollElement.scrollLeft = scrollElement.scrollLeft + 400;
    }
  };

  // Función para aplicar color a las filas según el estado
  const getRowClassName = (record) => {
    // Prioridad 1: Estado de auditoría (Facturado y Recibido)
    const estadoAuditoriaClasses = {
      facturado: 'row-facturado',     // Azul (máxima prioridad)
      recibido: 'row-recibido',       // Verde
    };

    if (record.estado_auditoria && estadoAuditoriaClasses[record.estado_auditoria]) {
      return estadoAuditoriaClasses[record.estado_auditoria];
    }

    // Prioridad 2: Si NO tiene estado_auditoria, considerar estado_servicio
    const estadoServicioClasses = {
      cancelado: 'row-cancelado',     // Rojo
      fallido: 'row-fallido',         // Vinotinto
    };

    if (!record.estado_auditoria && record.estado_servicio && estadoServicioClasses[record.estado_servicio]) {
      return estadoServicioClasses[record.estado_servicio];
    }

    // Default: Sin estado
    return 'row-sin-estado'; // Gris
  };

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Título y acciones principales */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600', color: '#0a2e5c' }}>
            Control de Servicios
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#8c8c8c' }}>
            Gestión y control de facturación de servicios
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#1890ff' }}>
            📅 Mostrando registros del día: {dayjs(filtros.fecha_inicio).format('DD/MM/YYYY')}
            {filtros.fecha_inicio !== filtros.fecha_fin && ` al ${dayjs(filtros.fecha_fin).format('DD/MM/YYYY')}`}
          </p>
        </div>
        <Space>
          {selectedRowKeys.length >= 2 && hasPermission('FACTURADO_ETIQUETA_CS') && (
            <Badge count={selectedRowKeys.length} offset={[-10, 10]}>
              <Button
                size="large"
                icon={<DollarOutlined />}
                onClick={handleFacturarMasivo}
                style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
              >
                Facturar Masivamente
              </Button>
            </Badge>
          )}
          <Button
            size="large"
            icon={<FileExcelOutlined />}
            onClick={handleAbrirExportarRango}
          >
            Exportar
          </Button>
          {selectedRowKeys.length > 0 && (
            <Badge count={selectedRowKeys.length} offset={[-10, 10]}>
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleExportacionMasiva}
              >
                Exportar Seleccionados
              </Button>
            </Badge>
          )}
        </Space>
      </div>

      {/* Cards de Estadísticas */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Total Controles"
              value={estadisticas.total}
              valueStyle={{ color: '#0a2e5c', fontWeight: 'bold' }}
              prefix={<FileProtectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Terminados"
              value={estadisticas.terminados}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Fallidos"
              value={estadisticas.fallidos}
              valueStyle={{ color: '#9B1C31', fontWeight: 'bold' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Cancelados"
              value={estadisticas.cancelados}
              valueStyle={{ color: '#ff4d4f', fontWeight: 'bold' }}
              prefix={<StopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Sin Estado"
              value={estadisticas.sin_estado}
              valueStyle={{ color: '#8c8c8c' }}
              prefix={<MinusCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Recibidos"
              value={estadisticas.recibidos}
              valueStyle={{ color: '#13c2c2' }}
              prefix={<FileProtectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Facturados"
              value={estadisticas.facturados}
              valueStyle={{ color: '#1890ff' }}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Validados"
              value={estadisticas.validados}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={{ flex: '1 1 0' }}>
          <Card>
            <Statistic
              title="Sin Validar"
              value={estadisticas.sin_validar}
              valueStyle={{ color: '#faad14' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Card principal con filtros y tabla */}
      <Card
        bodyStyle={{ padding: '24px' }}
        style={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Barra de Filtros */}
        <div style={{
          background: '#fafafa',
          padding: '12px 16px',
          borderRadius: '8px',
          border: '1px solid #e8e8e8'
        }}>
          <Row gutter={[8, 8]} align="middle">
            <Col flex="200px">
              <Search
                placeholder="Buscar..."
                value={filtros.search}
                onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
                onSearch={handleAplicarFiltros}
                allowClear
                enterButton
              />
            </Col>
            <Col flex="250px">
              <RangePicker
                format="DD/MM/YYYY"
                placeholder={['Inicio', 'Fin']}
                style={{ width: '100%' }}
                value={[
                  filtros.fecha_inicio ? dayjs(filtros.fecha_inicio) : null,
                  filtros.fecha_fin ? dayjs(filtros.fecha_fin) : null
                ]}
                onChange={(dates) =>
                  setFiltros({
                    ...filtros,
                    fecha_inicio: dates?.[0]?.format('YYYY-MM-DD'),
                    fecha_fin: dates?.[1]?.format('YYYY-MM-DD'),
                  })
                }
              />
            </Col>
            <Col flex="130px">
              <Select
                placeholder="Servicio"
                style={{ width: '100%' }}
                value={filtros.estado_servicio}
                onChange={(value) => setFiltros({ ...filtros, estado_servicio: value })}
                allowClear
              >
                <Option value="terminado">
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                  Terminado
                </Option>
                <Option value="fallido">
                  <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: '4px' }} />
                  Fallido
                </Option>
                <Option value="cancelado">
                  <StopOutlined style={{ color: '#faad14', marginRight: '4px' }} />
                  Cancelado
                </Option>
              </Select>
            </Col>
            <Col flex="130px">
              <Select
                placeholder="Auditoría"
                style={{ width: '100%' }}
                value={filtros.tag}
                onChange={(value) => setFiltros({ ...filtros, tag: value })}
                allowClear
              >
                <Option value="sin_estado">
                  <MinusCircleOutlined style={{ color: '#8c8c8c', marginRight: '4px' }} />
                  Sin estado
                </Option>
                <Option value="recibido">
                  <FileProtectOutlined style={{ color: '#13c2c2', marginRight: '4px' }} />
                  Recibido
                </Option>
                <Option value="facturado">
                  <DollarOutlined style={{ color: '#1890ff', marginRight: '4px' }} />
                  Facturado
                </Option>
              </Select>
            </Col>
            <Col flex="145px">
              <Select
                placeholder="Validación"
                style={{ width: '100%' }}
                value={filtros.seguimiento_traslado}
                onChange={(value) => setFiltros({ ...filtros, seguimiento_traslado: value })}
                allowClear
              >
                <Option value="Validado">
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                  Validado
                </Option>
                <Option value="Sin Validar">
                  <WarningOutlined style={{ color: '#faad14', marginRight: '4px' }} />
                  Sin Validar
                </Option>
              </Select>
            </Col>
            <Col flex="180px">
              <Select
                placeholder="Zona"
                style={{ width: '100%' }}
                value={filtros.zona}
                onChange={(value) => setFiltros({ ...filtros, zona: value })}
                allowClear
                loading={zonasLoading}
                disabled={zonasLoading}
              >
                {zonasUsuario.map((zona) => (
                  <Option key={zona.id} value={zona.nombre}>
                    <EnvironmentOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                    {zona.nombre}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col flex="180px">
              <Select
                showSearch
                placeholder="EPS"
                style={{ width: '100%' }}
                value={filtros.eps}
                onChange={(value) => setFiltros({ ...filtros, eps: value || null })}
                onDropdownVisibleChange={handleEpsFilterOpen}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                allowClear
              >
                {epsList.map((eps) => (
                  <Option key={eps.id} value={eps.nombre_cliente}>
                    {eps.nombre_cliente}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col flex="100px">
              <Select
                placeholder="Tipo"
                style={{ width: '100%' }}
                value={filtros.tipo_traslado}
                onChange={(value) => setFiltros({ ...filtros, tipo_traslado: value })}
                allowClear
              >
                <Option value="tai">
                  <CarOutlined style={{ color: '#722ed1', marginRight: '4px' }} />
                  TAI
                </Option>
                <Option value="normal">
                  <CarOutlined style={{ color: '#8c8c8c', marginRight: '4px' }} />
                  Normal
                </Option>
              </Select>
            </Col>
            <Col flex="150px">
              <AutoComplete
                placeholder="Móvil"
                style={{ width: '100%' }}
                value={filtros.movil}
                onChange={(value) => setFiltros({ ...filtros, movil: value || null })}
                onDropdownVisibleChange={handleMovilFilterOpen}
                options={vehiculos.map((v) => ({
                  label: (
                    <span>
                      <CarOutlined style={{ color: '#1890ff', marginRight: '4px' }} />
                      {v.codigo} - {v.placa}
                    </span>
                  ),
                  value: v.codigo,
                }))}
                allowClear
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Col>
            <Col flex="auto">
              <Space size="small">
                <Checkbox
                  checked={filtros.con_novedades_pendientes}
                  onChange={(e) => setFiltros({ ...filtros, con_novedades_pendientes: e.target.checked, con_novedades_resueltas: false })}
                  style={{
                    padding: '4px 8px',
                    background: filtros.con_novedades_pendientes ? '#fff2e8' : 'white',
                    borderRadius: '6px',
                    border: filtros.con_novedades_pendientes ? '1px solid #ffbb96' : '1px solid #d9d9d9',
                  }}
                >
                  <WarningOutlined style={{ color: '#ff4d4f', marginRight: '4px' }} />
                  <span style={{
                    color: filtros.con_novedades_pendientes ? '#ff4d4f' : 'inherit',
                    fontWeight: filtros.con_novedades_pendientes ? '600' : 'normal',
                    fontSize: '13px'
                  }}>
                    Pendientes
                  </span>
                </Checkbox>
                <Checkbox
                  checked={filtros.con_novedades_resueltas}
                  onChange={(e) => setFiltros({ ...filtros, con_novedades_resueltas: e.target.checked, con_novedades_pendientes: false })}
                  style={{
                    padding: '4px 8px',
                    background: filtros.con_novedades_resueltas ? '#f6ffed' : 'white',
                    borderRadius: '6px',
                    border: filtros.con_novedades_resueltas ? '1px solid #b7eb8f' : '1px solid #d9d9d9',
                  }}
                >
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                  <span style={{
                    color: filtros.con_novedades_resueltas ? '#52c41a' : 'inherit',
                    fontWeight: filtros.con_novedades_resueltas ? '600' : 'normal',
                    fontSize: '13px'
                  }}>
                    Subsanadas
                  </span>
                </Checkbox>
              </Space>
            </Col>
            <Col flex="40px">
              <Tooltip title="Limpiar filtros">
                <Button
                  icon={<ClearOutlined />}
                  onClick={handleLimpiarFiltros}
                />
              </Tooltip>
            </Col>
          </Row>
        </div>

        {/* Botones de scroll horizontal */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '8px',
          gap: '8px'
        }}>
          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>Desplazar tabla:</span>
          <Button
            type="default"
            size="small"
            icon={<LeftOutlined />}
            onClick={handleScrollLeft}
          />
          <Button
            type="default"
            size="small"
            icon={<RightOutlined />}
            onClick={handleScrollRight}
          />
        </div>

        {/* Tabla */}
        <div ref={tableContainerRef}>
          <Table
            columns={columns}
            dataSource={controles}
            loading={loading}
            rowKey="id"
            rowSelection={rowSelection}
            rowClassName={getRowClassName}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} registros`,
            }}
            onChange={(newPagination) => setPagination(newPagination)}
            scroll={{ x: 2800 }}
          />
        </div>
      </Space>

      {/* Modal de Historial del Control de Servicio */}
      <HistorialControlServicioDialog
        open={historialVisible}
        onClose={() => {
          setHistorialVisible(false);
          setHistorialControlId(null);
          setHistorialControlCodigo(null);
        }}
        controlServicioId={historialControlId}
        codigoPcs={historialControlCodigo}
      />

      {/* Modal de Historial del Traslado (diseño moderno) */}
      <HistorialTrasladoDialog
        open={historialTrasladoVisible}
        onClose={() => {
          setHistorialTrasladoVisible(false);
          setHistorialTrasladoId(null);
        }}
        trasladoId={historialTrasladoId}
      />

      {/* Modal de Mapa */}
      {trasladoSeleccionado && (() => {
        const origin = {
          lat: parseFloat(trasladoSeleccionado.latitud_origen),
          lng: parseFloat(trasladoSeleccionado.longitud_origen)
        };
        const destination = {
          lat: parseFloat(trasladoSeleccionado.latitud_destino),
          lng: parseFloat(trasladoSeleccionado.longitud_destino)
        };

        console.log('Pasando coordenadas a MapDialog:', { origin, destination });

        return (
          <MapDialog
            key={`map-${trasladoSeleccionado.id}`}
            open={mapaVisible}
            onClose={() => {
              setMapaVisible(false);
              setTrasladoSeleccionado(null);
            }}
            origin={origin}
            destination={destination}
          />
        );
      })()}

      {/* Observaciones del traslado del control */}
      {observacionesTrasladoId && (
        <ObservacionesDialog
          key={`observaciones-${observacionesTrasladoId}`}
          open={!!observacionesTrasladoId}
          trasladoId={observacionesTrasladoId}
          onClose={() => setObservacionesTrasladoId(null)}
        />
      )}

      {/* Modal de Novedades */}
      <NovedadesModal
        key={`novedades-${controlSeleccionado?.id}`}
        visible={novedadesVisible}
        onClose={handleCerrarNovedades}
        controlServicio={controlSeleccionado}
      />

      {/* Modal de Ruta GPS Trazada */}
      {trasladoIdRuta && (
        <RutaTrazadaView
          key={`ruta-${trasladoIdRuta}`}
          visible={rutaTrazadaVisible}
          trasladoId={trasladoIdRuta}
          traslado={trasladoRuta}
          onClose={handleCerrarRutaTrazada}
        />
      )}

      {/* Modal de Tipo de Exportación */}
      <Modal
        title="Seleccione el tipo de exportación"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ marginBottom: '20px', fontSize: '14px' }}>
            Ha seleccionado <strong>{selectedRowKeys.length}</strong> control(es) de servicio.
            Elija cómo desea exportarlos:
          </p>

          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card
              hoverable
              onClick={handleExportarSeparados}
              style={{ cursor: 'pointer' }}
            >
              <Space direction="vertical" size="small">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FileZipOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                  <strong style={{ fontSize: '16px' }}>Exportar Separados</strong>
                </div>
                <p style={{ margin: 0, color: '#666' }}>
                  Descarga un archivo ZIP con un PDF individual por cada control de servicio seleccionado.
                </p>
              </Space>
            </Card>

            <Card
              hoverable
              onClick={handleExportarUnificado}
              style={{ cursor: 'pointer' }}
            >
              <Space direction="vertical" size="small">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
                  <strong style={{ fontSize: '16px' }}>Exportar Unificado</strong>
                </div>
                <p style={{ margin: 0, color: '#666' }}>
                  Descarga un solo PDF con todas las planillas (1 página por control de servicio).
                </p>
              </Space>
            </Card>

            <Card
              hoverable
              onClick={handleExportarExcelLinea}
              style={{ cursor: 'pointer' }}
            >
              <Space direction="vertical" size="small">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FileExcelOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                  <strong style={{ fontSize: '16px' }}>Exportar en Excel (línea)</strong>
                </div>
                <p style={{ margin: 0, color: '#666' }}>
                  Descarga un archivo Excel con una fila por control de servicio y todos sus datos en columnas.
                </p>
              </Space>
            </Card>
          </Space>

          <div style={{ marginTop: '20px', textAlign: 'right' }}>
            <Button onClick={() => setExportModalVisible(false)} disabled={exportLoading}>
              Cancelar
            </Button>
          </div>
        </div>

        {exportLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <Space direction="vertical" align="center">
              <div className="spinner" style={{ fontSize: '40px' }}>⏳</div>
              <p>Generando exportación...</p>
            </Space>
          </div>
        )}
      </Modal>

      {/* Modal de Exportar en Excel (línea) por rango de fechas */}
      <Modal
        title="Exportar en Excel (línea) por fechas"
        open={rangoExportVisible}
        onCancel={() => !rangoExportLoading && setRangoExportVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="cancelar" onClick={() => setRangoExportVisible(false)} disabled={rangoExportLoading}>
            Cancelar
          </Button>,
          <Button
            key="exportar"
            type="primary"
            icon={<DownloadOutlined />}
            loading={rangoExportLoading}
            onClick={handleExportarExcelRango}
          >
            Exportar
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Seleccione el rango de fechas:</p>
          <p style={{ color: '#8c8c8c', fontSize: 14, marginTop: -10 }}>
            El rango máximo permitido es de 1 mes
          </p>
        </div>
        <RangePicker
          style={{ width: '100%' }}
          format="DD/MM/YYYY"
          value={rangoExport}
          onChange={(fechas) => setRangoExport(fechas)}
          onCalendarChange={(fechas) => setRangoExportInicio(fechas?.[0] && !fechas?.[1] ? fechas[0] : null)}
          onOpenChange={(abierto) => !abierto && setRangoExportInicio(null)}
          disabledDate={rangoExportDeshabilitado}
          disabled={rangoExportLoading}
        />
        <p style={{ color: '#8c8c8c', fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          Incluye todos los controles de servicio del rango (según las zonas de su rol), una fila por control
          con todos sus datos en columnas. Un mes completo puede tardar cerca de un minuto.
        </p>
      </Modal>

      {/* Modal de Previsualización de PDF */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <span>Previsualización del Control de Servicio</span>
          </div>
        }
        open={previsualizacionVisible}
        onCancel={handleCerrarPrevisualizacion}
        footer={[
          <Button
            key="descargar"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              if (controlIdPrevisualizacion) {
                handleDescargarPDF(controlIdPrevisualizacion);
              }
            }}
          >
            Descargar PDF
          </Button>,
          <Button key="cerrar" onClick={handleCerrarPrevisualizacion}>
            Cerrar
          </Button>,
        ]}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{ padding: '0', height: 'calc(100vh - 200px)' }}
      >
        {previsualizacionLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '500px'
          }}>
            <Space direction="vertical" align="center">
              <div style={{ fontSize: '40px' }}>📄</div>
              <p>Cargando previsualización...</p>
            </Space>
          </div>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            style={{
              width: '100%',
              height: 'calc(100vh - 200px)',
              border: 'none'
            }}
            title="Previsualización del Control de Servicio"
          />
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '500px'
          }}>
            <p>No se pudo cargar el PDF</p>
          </div>
        )}
      </Modal>
    </Card>
    </div>
  );
};

export default ControlServiciosTable;
